/**
 * QuoteCustomerPreview — document-style customer quote preview (mock).
 */

import React, { useMemo } from "react";
import {
  Box,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from "@mui/material";
import { CheckCircleOutline } from "@mui/icons-material";
import { styled } from "@mui/material/styles";
import {
  formatQuoteMoney,
  getCustomerLineItems,
  getQuoteDeposit,
  getQuoteDistanceLabel,
  getQuoteMoveTypeLabel,
  getQuoteTotal,
  getQuoteWhatsIncluded,
} from "../services/quoteOperationalStatus.js";
import { formatQuoteMoveDate } from "../utils/moveDateUtils.js";

const PreviewShell = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2.5),
  borderRadius: 14,
  border: `1px solid ${theme.palette.divider}`,
  overflow: "hidden",
  backgroundColor: "#fff",
  boxShadow: "0 8px 28px rgba(15, 23, 42, 0.06)",
}));

const BrandBar = styled(Box)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "flex-start",
  gap: theme.spacing(2),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(2.5),
}));

const DepositCallout = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  padding: theme.spacing(2),
  borderRadius: 12,
  backgroundColor: "rgba(237, 108, 2, 0.1)",
  border: "1px solid rgba(237, 108, 2, 0.35)",
}));

const QuoteCustomerPreview = ({ quote }) => {
  const lines = useMemo(() => getCustomerLineItems(quote), [quote]);
  const total = useMemo(() => getQuoteTotal(quote), [quote]);
  const deposit = useMemo(() => getQuoteDeposit(quote), [quote]);
  const included = useMemo(() => getQuoteWhatsIncluded(quote), [quote]);

  if (!quote) return null;

  return (
    <PreviewShell elevation={0} data-testid="quote-customer-preview">
      <CardContent sx={{ p: { xs: 2.5, md: 3.5 } }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ letterSpacing: 1.2 }}
        >
          Customer quote preview
        </Typography>

        <BrandBar>
          <Box>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, color: "success.dark", letterSpacing: 0.5 }}
            >
              Bubakii
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Moving made simple
            </Typography>
          </Box>
          <Box textAlign="right">
            <Typography
              variant="overline"
              sx={{ fontWeight: 800, letterSpacing: 1.5 }}
            >
              Quote
            </Typography>
            <Typography
              variant="body2"
              sx={{ fontFamily: "monospace", fontWeight: 600 }}
            >
              {quote.id}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block">
              Valid for 7 days
            </Typography>
          </Box>
        </BrandBar>

        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
          {quote.from || "Origin"}
        </Typography>
        <Typography color="text.secondary" sx={{ my: 0.5, fontWeight: 700 }}>
          ↓
        </Typography>
        <Typography variant="h4" sx={{ fontWeight: 800, lineHeight: 1.2, mb: 2 }}>
          {quote.to || "Destination"}
        </Typography>

        <Box display="flex" flexWrap="wrap" gap={3} mb={2.5}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Move date
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {formatQuoteMoveDate(quote)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Distance
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {getQuoteDistanceLabel(quote)}
            </Typography>
          </Box>
          <Box>
            <Typography variant="caption" color="text.secondary">
              Move type
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {getQuoteMoveTypeLabel(quote)}
            </Typography>
          </Box>
        </Box>

        <Box mb={2}>
          {lines.map((line, index) => (
            <Box
              key={`${line.description}-${index}`}
              display="flex"
              justifyContent="space-between"
              gap={2}
              py={0.75}
              borderBottom="1px solid"
              borderColor="divider"
            >
              <Typography variant="body2">{line.description}</Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formatQuoteMoney(line.amount)}
              </Typography>
            </Box>
          ))}
          <Box
            display="flex"
            justifyContent="space-between"
            gap={2}
            pt={1.25}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              TOTAL
            </Typography>
            <Typography
              variant="h5"
              sx={{ fontWeight: 800, color: "success.dark" }}
            >
              {formatQuoteMoney(total)}
            </Typography>
          </Box>
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
          What&apos;s included
        </Typography>
        <List dense disablePadding sx={{ mb: 1 }}>
          {included.slice(0, 4).map((item) => (
            <ListItem key={item} disableGutters sx={{ py: 0.15 }}>
              <ListItemIcon sx={{ minWidth: 28 }}>
                <CheckCircleOutline color="success" sx={{ fontSize: 18 }} />
              </ListItemIcon>
              <ListItemText
                primary={item}
                primaryTypographyProps={{ variant: "body2" }}
              />
            </ListItem>
          ))}
        </List>

        <DepositCallout>
          <Typography
            variant="caption"
            sx={{ fontWeight: 700, letterSpacing: 0.8, color: "warning.dark" }}
          >
            DEPOSIT REQUIRED
          </Typography>
          <Typography
            variant="h4"
            sx={{ fontWeight: 800, color: "warning.dark" }}
          >
            {deposit > 0 ? formatQuoteMoney(deposit) : "—"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Pay the deposit to secure your move date.
          </Typography>
        </DepositCallout>

        <Divider sx={{ my: 2.5 }} />
        <Typography variant="caption" color="text.secondary" display="block">
          Need help?
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          WhatsApp · {quote.customerPhone || "support"} · hello@bubakii.com
        </Typography>
      </CardContent>
    </PreviewShell>
  );
};

export default QuoteCustomerPreview;
